import React from "react";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Modal } from "../../../components/ui";
import { useClientsStore } from "./use-clients-store";
import { blockClientSchema, type BlockClientForm } from "./clients-types";

const Title = styled.h3`
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
  color: #202020;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  resize: vertical;
  outline: none;
  font-family: inherit;
  &:focus {
    border-color: #e65100;
  }
`;

const ErrorText = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #c62828;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const Btn = styled.button<{ $variant: "primary" | "secondary" }>`
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid
    ${(p) => (p.$variant === "primary" ? "#e65100" : "#e0e0e0")};
  background: ${(p) => (p.$variant === "primary" ? "#e65100" : "white")};
  color: ${(p) => (p.$variant === "primary" ? "white" : "#333")};
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const ModalBlockClient: React.FC = () => {
  const { selectedClient, modalBlock, setModalBlock, blockClient } =
    useClientsStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<BlockClientForm>({
    resolver: zodResolver(blockClientSchema),
  });

  if (!modalBlock || !selectedClient) return null;

  const onSubmit = async (data: BlockClientForm) => {
    await blockClient(selectedClient.uuid, data.reason);
  };

  const clientName =
    selectedClient.profile?.first_name || selectedClient.email || selectedClient.uuid;

  return (
    <Modal
      handleClose={() => setModalBlock(false)}
      dialogStyle={{ maxWidth: 440 }}
    >
      <Title>Заблокировать пользователя</Title>
      <p style={{ fontSize: 13, color: "#666", margin: "0 0 12px" }}>
        Вы собираетесь заблокировать <strong>{clientName}</strong>
      </p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Label>Причина блокировки</Label>
        <TextArea
          {...register("reason")}
          placeholder="Укажите причину блокировки..."
        />
        {errors.reason && <ErrorText>{errors.reason.message}</ErrorText>}
        <ButtonRow>
          <Btn $variant="secondary" type="button" onClick={() => setModalBlock(false)}>
            Отмена
          </Btn>
          <Btn $variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Блокировка..." : "Заблокировать"}
          </Btn>
        </ButtonRow>
      </form>
    </Modal>
  );
};
